import { decode } from 'html-entities';

import { getNumericRiskValue } from 'src/hooks/useDashboardFilters';
import { ScoreWindow } from 'src/types';

const descendingComparator = (
  a: any,
  b: any,
  orderBy: string,
  riskKeyByTitleMap: Record<string, string>
) => {
  if (!orderBy) {
    return 0;
  }

  const orderByKey = riskKeyByTitleMap[orderBy];

  const getValue = (item: any) =>
    orderBy.toLocaleLowerCase() === 'name'
      ? item.show
      : orderBy.toLocaleLowerCase() === 'episode title'
      ? item['Episode Title'].toLocaleLowerCase()
      : item[orderByKey]?.id
      ? getNumericRiskValue(item[orderByKey]?.medianRisk)
      : getNumericRiskValue(item[orderByKey]);
  if (
    orderBy.toLocaleLowerCase() === 'name' ||
    orderBy.toLocaleLowerCase() === 'episode title'
  ) {
    return getValue(b) > getValue(a) ? 1 : -1;
  } else if (orderBy.toLocaleLowerCase() === 'episode date') {
    const aDate = new Date(a['Episode Date']).getTime() || 0;
    const bDate = new Date(b['Episode Date']).getTime() || 0;
    return bDate > aDate ? 1 : -1;
  } else {
    return getValue(b) - getValue(a);
  }
};

export const getComparator = (
  order: 'desc' | 'asc' | undefined,
  orderBy: string,
  riskKeyByTitleMap: Record<string, string>
) => {
  return order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy, riskKeyByTitleMap)
    : (a: any, b: any) =>
        -descendingComparator(a, b, orderBy, riskKeyByTitleMap);
};

export const stableSort = (array: any[], comparator: any) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export const decodePercentU = (str: string) => {
  return str.replace(/%u([0-9A-F]{4})/gi, (_, code) =>
    String.fromCharCode(parseInt(code, 16))
  );
};

// UTF-8 bytes that were decoded as Windows-1252/ISO-8859-1.
const WINDOWS_1252_TO_BYTE: Record<number, number> = {
  0x20ac: 0x80, // €
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99, // ™
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

const toMojibakeByte = (code: number): number | null => {
  if (code <= 0xff) return code;
  return WINDOWS_1252_TO_BYTE[code] ?? null;
};

const repairUtf8Mojibake = (str: string): string => {
  const bytes: number[] = [];
  for (const ch of str) {
    const byte = toMojibakeByte(ch.charCodeAt(0));
    if (byte === null) return str;
    bytes.push(byte);
  }

  try {
    const repaired = new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(bytes)
    );
    return repaired || str;
  } catch {
    return str;
  }
};

const repairBrokenPunctuation = (str: string): string =>
  str
    // leftover curly-apostrophe mojibake when trailing bytes became U+FFFD
    .replace(/â\uFFFD{1,2}/g, '\'')
    .replace(/â€™|â\u20AC\u2122/g, '\'')
    .replace(/â€˜/g, '\'')
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€“/g, '–')
    .replace(/â€”/g, '—')
    // lost byte already replaced with � between letters (O�Brien)
    .replace(/(\w)\uFFFD(\w)/g, '$1\'$2')
    // Irish O' prefix with the apostrophe stripped (OBrien)
    .replace(/\bO([A-Z][a-z]+)/g, 'O\'$1');

const scoreStatsKey = (window: ScoreWindow) =>
  window === 'lifetime' ? 'lifetimeStats' : 'last12MonthsStats';

/** Copy the selected precomputed score blob onto the row fields the table reads. */
export const applyScoreWindow = (item: any, window: ScoreWindow) => {
  const stats = item?.[scoreStatsKey(window)];
  const next = { ...item, windowStatsMissing: !stats };
  if (!stats || typeof stats !== 'object') {
    return next;
  }

  Object.keys(stats).forEach((key) => {
    const historyValues = stats[key];
    if (!historyValues) {
      return;
    }
    if (key === 'electionsPercentage') {
      next[key] = {
        ...historyValues,
        value: historyValues.medianRiskValue,
        up: historyValues.episodeCountHigherThanAverage,
        down: historyValues.episodeCountLowerThanAverage,
      };
      return;
    }

    const historyScore = item.riskHistories?.current?.[key]?.medianRiskValue;
    const oldScore = item.riskHistories?.prev?.[key]?.medianRiskValue;
    next[key] = {
      ...historyValues,
      score: historyValues.medianRiskValue,
      oldScore,
      historyScore,
      updatedShow:
        typeof historyScore === 'number' &&
        typeof oldScore === 'number' &&
        historyScore !== oldScore,
    };
  });

  return next;
};

export const displayShowName = (name: string): string => {
  if (!name) return '';
  return repairBrokenPunctuation(
    repairUtf8Mojibake(decodePercentU(decode(name)))
  );
};
